import { Alert, Button, CircularProgress, Stack, TextField as MuiTextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { OtpField, TextField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { GoogleSignInButton } from '@/features/identity/components/GoogleSignInButton';
import { useForgotMfa } from '@/features/identity/hooks/useForgotMfa';
import { useOAuthMfaRecovery } from '@/features/identity/hooks/useOAuthMfaRecovery';

/**
 * The pre-authentication lost-authenticator reset screen — for a caller stuck at the MFA challenge with no
 * code that works, and no way to reach the authenticated reset dialog.
 *
 * <p>Two routes, and the user says which one applies before either is shown. Asking the server instead
 * would answer "does this address have an account, and how does it sign in?" for any address typed in, so
 * the choice is the user's to make and a wrong guess costs only a click back.</p>
 *
 * <p><b>The password asked for is the WillnQ password, and the copy says so everywhere.</b> An account
 * created through Google has no WillnQ password at all, and a field labelled just "Password" on a screen
 * reached from a Google sign-in invites exactly the wrong credential. Google's own password is never typed
 * into WillnQ: that route hands off to Google, which returns a token, and the token is all WillnQ sees.</p>
 */
export function ForgotMfaForm() {
  const {
    stage,
    setStage,
    email,
    setEmail,
    resetVia,
    setResetVia,
    emailForm,
    passwordForm,
    otpForm,
    onSubmitEmail,
    onSubmitPassword,
    onSubmitOtp,
    backToMethod,
    serverError,
  } = useForgotMfa();

  const google = useOAuthMfaRecovery({
    provider: 'Google',
    // The account is resolved server-side from the Google token's provider subject, so the address shown
    // on the confirmation screen is the one on the account found — never the one typed, which selects
    // nothing.
    onVerified: (accountEmail) => {
      setEmail(accountEmail);
      setStage('confirm');
    },
    onReset: () => {
      setResetVia('google');
      setStage('done');
    },
  });

  const backToSignIn = (
    <>
      Remembered your authenticator? <RouterLink to={ROUTES.login}>Sign in</RouterLink>
    </>
  );

  if (stage === 'done') {
    return (
      <AuthCard>
        <AuthHeader title="Authenticator reset" />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {resetVia === 'google'
            ? "Your authenticator has been removed and you've been signed out everywhere. Sign in again with Google. If your role requires MFA, you'll be prompted to set up a new authenticator right away."
            : "Your authenticator has been removed and you've been signed out everywhere. Sign in again with your WillnQ password. If your role requires MFA, you'll be prompted to set up a new authenticator right away."}
        </Typography>
        <Button component={RouterLink} to={ROUTES.login} variant="contained">
          Back to sign in
        </Button>
      </AuthCard>
    );
  }

  // The explicit gate on the Google route. A verified Google identity got the user here; nothing has been
  // changed yet, and leaving now leaves the account exactly as it was.
  if (stage === 'confirm') {
    return (
      <AuthCard>
        <AuthHeader title="Reset your authenticator?" subtitle={`Signed in as ${email}.`} />
        <Stack spacing={2}>
          {google.error ? <Alert severity="error">{google.error}</Alert> : null}
          <Typography variant="body2" color="text.secondary">
            Your current authenticator will stop working and you&apos;ll be signed out on every device.
            You&apos;ll set up a new authenticator the next time you sign in.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => void google.reset()}
            disabled={google.isBusy}
            startIcon={google.isBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Reset MFA
          </Button>
          <Button variant="text" component={RouterLink} to={ROUTES.login} disabled={google.isBusy}>
            Cancel
          </Button>
        </Stack>
      </AuthCard>
    );
  }

  if (stage === 'otp') {
    return (
      <AuthCard>
        <AuthHeader
          title="Enter the code we emailed you"
          // Deliberately conditional. The server answers identically whether the password was right,
          // wrong, or the account has no WillnQ password at all, so this screen genuinely cannot know a
          // code was issued — and stating one was, to someone whose account is Google-only and who will
          // therefore never receive it, is a dead end that reads as a success.
          subtitle={`If that WillnQ password was correct, we've emailed a 6-digit code to ${email}. Entering it removes your current authenticator and signs you out everywhere.`}
        />
        <Stack component="form" onSubmit={onSubmitOtp} spacing={2} noValidate alignItems="center">
          {serverError ? (
            <Alert severity="error" sx={{ width: '100%' }}>
              {serverError}
            </Alert>
          ) : null}
          <OtpField name="otp" control={otpForm.control} />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={otpForm.formState.isSubmitting}
            startIcon={
              otpForm.formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            Reset authenticator
          </Button>
          {/* The escape hatch. Someone who signed up with Google and guessed at a WillnQ password lands
              here waiting for an email that is never coming; this is how they get back out. */}
          <Button variant="text" size="small" onClick={backToMethod}>
            No code arriving? Recover with Google instead
          </Button>
        </Stack>
      </AuthCard>
    );
  }

  if (stage === 'password') {
    return (
      <AuthCard footer={backToSignIn}>
        <AuthHeader
          title="Enter your WillnQ password"
          subtitle="This is the password you set for WillnQ, not your Google password. We'll email you a code to reset your authenticator."
        />
        <Stack component="form" onSubmit={onSubmitPassword} spacing={2} noValidate>
          {serverError ? <Alert severity="error">{serverError}</Alert> : null}
          <MuiTextField label="Email" type="email" value={email} disabled fullWidth />
          <TextField
            name="password"
            control={passwordForm.control}
            label="WillnQ password"
            type="password"
            autoComplete="current-password"
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={passwordForm.formState.isSubmitting}
            startIcon={
              passwordForm.formState.isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Email me a code
          </Button>
          <Button variant="text" size="small" onClick={backToMethod} disabled={passwordForm.formState.isSubmitting}>
            Back
          </Button>
        </Stack>
      </AuthCard>
    );
  }

  // The choice itself. No credential field is rendered here, so opening this flow cannot prompt a password
  // manager, and neither route is presented as the default one.
  if (stage === 'method') {
    return (
      <AuthCard footer={backToSignIn}>
        <AuthHeader
          title="How would you like to verify your identity?"
          subtitle={`Recovering the authenticator for ${email}.`}
        />
        <Stack spacing={2}>
          {google.error ? <Alert severity="error">{google.error}</Alert> : null}

          <Stack spacing={0.5}>
            <GoogleSignInButton
              label="Continue with Google"
              onSuccess={google.verifyWithToken}
              disabled={google.isBusy}
            />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Use your Google account
            </Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => setStage('password')}
              disabled={google.isBusy}
            >
              Use my WillnQ password
            </Button>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Use the password for your WillnQ account
            </Typography>
          </Stack>
        </Stack>
      </AuthCard>
    );
  }

  return (
    <AuthCard footer={backToSignIn}>
      <AuthHeader title="Lost your authenticator?" subtitle="Enter your email to get started." />
      <Stack component="form" onSubmit={onSubmitEmail} spacing={2} noValidate>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        <TextField
          name="email"
          control={emailForm.control}
          label="Email"
          type="email"
          autoComplete="email"
        />
        <Button type="submit" variant="contained" size="large">
          Continue
        </Button>
      </Stack>
    </AuthCard>
  );
}
